import { useState, useEffect } from 'react';

/**
 * Hook to observe media query matches.
 * Returns true when the media query matches.
 *
 * Ensures cross-browser compatibility with fallbacks for older Safari versions.
 */
function useMediaQuery(query) {
    const [matches, setMatches] = useState(false);
    useEffect(() => {
        // SSR guard - return early if window is not available
        if (typeof window === "undefined" || !window.matchMedia) {
            return;
        }
        const mediaQuery = window.matchMedia(query);
        // Update state with current value
        const updateMatches = () => setMatches(mediaQuery.matches);
        updateMatches();
        // Modern browsers support addEventListener
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", updateMatches);
            return () => mediaQuery.removeEventListener("change", updateMatches);
        }
        // Fallback for older Safari versions - use try/catch for safety
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            mediaQuery.addListener(updateMatches);
            return () => {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    mediaQuery.removeListener(updateMatches);
                }
                catch (_a) {
                    // Ignore errors during cleanup
                }
            };
        }
        catch (_a) {
            // If legacy methods fail, we'll just use the initial value
        }
    }, [query]);
    return matches;
}

export { useMediaQuery };
